/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Popover } from "react-bootstrap";

import { ReactComponent as CheckmarkCircle } from "assets/images/icons/CheckmarkCircle.svg";
import { ReactComponent as XLSX } from "assets/images/icons/TopNavIcons/XLSX.svg";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import { getAllReviews, getProvidersList, getAllProviderContacts } from "api";

type CommonProviderListingOptions = {
  useQueryHook: any;
};

function useCommonProviderListing(options: CommonProviderListingOptions) {
  const { useQueryHook } = options;

  const sortedLocations = useSortedPaymentLocations();
  const { value: locationValue } = sortedLocations.find((location) => location.is_active);

  const intl = useIntl();
  const [freightModes, setFreightModes] = useState([]);
  const [locationReferenceNumber, setLocationReferenceNumber] = useState(locationValue);

  const staticColumns = [
    {
      Header: intl.formatMessage({ id: "settings.provider.providerNameHeader", defaultMessage: "Provider Name" }),
      accessor: "name",
      Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0 mr-1 font-weight-bold">{value}</p>,
    },
  ];

  const dynamicColumns = Object.values(freightModes).map((mode: any) => ({
    Header: mode.name,
    accessor: mode.name,
    Cell: ({ row }: any) => {
      const active = (Object.values(row.original.freight_modes).find((item: any) => item.name === mode.name) as any)?.value;
      return (
        <p className="pl-9 pt-8 pb-8 mb-0 mr-1" style={{ minWidth: "80px" }}>
          {active === "Yes" ? <CheckmarkCircle /> : "-"}
        </p>
      );
    },
  }));

  const columns = useMemo(
    () => (Object.values(freightModes).length > 0 ? [...staticColumns, ...dynamicColumns] : staticColumns),
    [freightModes],
  );

  const { instance, loading, totalData, refetch } = useTableQueryRequest(useQueryHook, columns, {
    useDefaultSorting: false,
    params: { location: locationReferenceNumber },
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
    }),
  });

  useEffect(() => {
    if (instance?.data[0]?.freight_modes) setFreightModes(instance?.data[0]?.freight_modes);
  }, [instance.data]);

  const exportData = (getData: any) => {
    const config = { responseType: "blob" };
    getData(config).then((res: any) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      const contentDisposition = res?.headers?.["content-disposition"];
      let fileName = `${Date.now()}.xlsx`;

      if (contentDisposition) {
        const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, "").trim();
        }
      }

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  };

  const popover = (
    <Popover
      id="popover-trigger-click"
      className="export-popover"
      style={{
        width: "191px",
        border: "none",
        padding: "16px",
        boxShadow: "0px 2px 8px rgba(0,0,0, 0.15)",
      }}
    >
      <Popover.Content style={{ padding: 0 }}>
        <span className="font-weight-bold" style={{ fontSize: "15px" }}>
          <FormattedMessage id="general.export" defaultMessage="Export" />
        </span>
        <div className="d-flex flex-column align-items-start">
          <div
            onClick={() => {
              exportData(getAllReviews);
            }}
            className="export-tab-item"
          >
            <XLSX />
            <span className="font-12 ml-2 justify-content-center">
              <FormattedMessage id="settings.provider.exportAllReviews" defaultMessage="Export all reviews" />
            </span>
          </div>
        </div>
        <div className="d-flex flex-column">
          <div
            onClick={() => {
              exportData(getProvidersList);
            }}
            className="export-tab-item"
          >
            <XLSX />
            <span className="font-12 ml-2">
              <FormattedMessage id="settings.provider.exportProvidersToXlsx" defaultMessage="Export Providers to XLSX" />
            </span>
          </div>
        </div>
        <div className="d-flex flex-column">
          <div
            onClick={() => {
              exportData(getAllProviderContacts);
            }}
            className="export-tab-item"
          >
            <XLSX />
            <span className="font-12 ml-2">
              <FormattedMessage id="settings.provider.exportProviderContacts" defaultMessage="Export Provider Contacts" />
            </span>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );

  return {
    instance,
    loading,
    totalData,
    locationReferenceNumber,
    setLocationReferenceNumber,
    refetch,
    popover,
  };
}

export default useCommonProviderListing;
